import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getSession } from '../services/authService';
import { isPlatformAdminEmail } from '../constants/platformAdmin';
import {
  type ProfileBubbleCategory,
  type ProfileBubbleMessage,
  createProfileBubbleCategory,
  createProfileBubbleMessage,
  deleteProfileBubbleCategory,
  deleteProfileBubbleMessage,
  listProfileBubbleCategories,
  listProfileBubbleMessages,
  setProfileBubbleCategoryShown,
  updateProfileBubbleMessage,
} from '../services/profileBubbleMessageService';

const BODY_MAX_LENGTH = 300;
const CATEGORY_NAME_MAX = 40;
const NEW_BUBBLE_ID = '__new__';

function actionErrorMessage(e: unknown, fallback: string): string {
  const code = e && typeof e === 'object' && 'code' in e ? String((e as { code?: unknown }).code ?? '') : '';
  if (code === '23505') return '같은 이름의 카테고리가 이미 있습니다.';
  return `${fallback}: ${e instanceof Error ? e.message : String(e)}`;
}

function formatDateShort(isoStr: string): string {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return isoStr;
  return `${d.getFullYear()}. ${d.getMonth() + 1}. ${d.getDate()}.`;
}

/** 말풍선 안에서 바로 편집 — 실제 사이드바 말풍선과 같은 폭·글꼴로 줄바꿈 확인 */
function BubbleEditor({
  initialBody,
  saving,
  onCommit,
  onCancel,
}: {
  initialBody: string;
  saving: boolean;
  onCommit: (body: string) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(initialBody);
  const ref = useRef<HTMLTextAreaElement>(null);
  const settledRef = useRef(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  const commit = () => {
    if (settledRef.current) return;
    settledRef.current = true;
    onCommit(draft);
  };

  const cancel = () => {
    if (settledRef.current) return;
    settledRef.current = true;
    onCancel();
  };

  return (
    <textarea
      ref={ref}
      className="cp-adm-bubble-textarea"
      value={draft}
      rows={1}
      maxLength={BODY_MAX_LENGTH}
      placeholder="말풍선 문구 입력"
      disabled={saving}
      aria-label="말풍선 문구"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancel();
        }
      }}
    />
  );
}

const AdminProfileBubblesPage: React.FC = () => {
  const [sessionEmail, setSessionEmail] = useState('');
  const [categories, setCategories] = useState<ProfileBubbleCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [addingCategory, setAddingCategory] = useState(false);
  const [categoryDraft, setCategoryDraft] = useState('');
  const [categorySaving, setCategorySaving] = useState(false);
  const categoryCancelRef = useRef(false);
  const categoryCreatingRef = useRef(false);
  const [list, setList] = useState<ProfileBubbleMessage[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const isAdmin = useMemo(() => isPlatformAdminEmail(sessionEmail), [sessionEmail]);

  useEffect(() => {
    let cancelled = false;
    void getSession()
      .then((s) => { if (!cancelled) setSessionEmail((s?.user?.email ?? '').trim().toLowerCase()); })
      .catch(() => { if (!cancelled) setSessionEmail(''); });
    return () => { cancelled = true; };
  }, []);

  const loadList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const [nextCategories, nextMessages] = await Promise.all([
        listProfileBubbleCategories(),
        listProfileBubbleMessages(),
      ]);
      setCategories(nextCategories);
      setList(nextMessages);
      setSelectedCategoryId((prev) => {
        if (prev && nextCategories.some((c) => c.id === prev)) return prev;
        return nextCategories[0]?.id ?? null;
      });
    } catch (e) {
      setListError(e instanceof Error ? e.message : '조회 실패');
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => { void loadList(); }, [loadList]);

  const selectedCategory = categories.find((c) => c.id === selectedCategoryId) ?? null;
  const visibleMessages = useMemo(
    () => list.filter((m) => m.category_id === selectedCategoryId),
    [list, selectedCategoryId]
  );

  const handleCommit = useCallback(async (id: string, rawBody: string) => {
    const body = rawBody.trim();
    setEditingId(null);
    const existing = list.find((m) => m.id === id);
    if (!body || (existing && existing.body === body)) return;
    setSavingId(id);
    setActionError(null);
    try {
      if (id === NEW_BUBBLE_ID) {
        if (!selectedCategoryId) { setActionError('카테고리를 먼저 선택하세요.'); return; }
        const created = await createProfileBubbleMessage(selectedCategoryId, body);
        setList((prev) => [...prev, created]);
      } else {
        const updated = await updateProfileBubbleMessage(id, body);
        setList((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
      }
    } catch (e) {
      setActionError(`저장 실패: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSavingId(null);
    }
  }, [list, selectedCategoryId]);

  const handleCreateCategory = useCallback(async (rawName: string) => {
    if (categoryCancelRef.current) {
      categoryCancelRef.current = false;
      return;
    }
    if (categoryCreatingRef.current) return;
    const name = rawName.trim();
    if (!name) {
      setAddingCategory(false);
      setCategoryDraft('');
      return;
    }
    categoryCreatingRef.current = true;
    setCategorySaving(true);
    setActionError(null);
    try {
      const created = await createProfileBubbleCategory(name);
      setCategories((prev) => [...prev, created].sort((a, b) => a.sort_order - b.sort_order));
      setSelectedCategoryId(created.id);
      setEditingId(null);
      setAddingCategory(false);
      setCategoryDraft('');
    } catch (e) {
      setActionError(actionErrorMessage(e, '카테고리 추가 실패'));
    } finally {
      categoryCreatingRef.current = false;
      setCategorySaving(false);
    }
  }, []);

  const handleDeleteCategory = useCallback(async (category: ProfileBubbleCategory) => {
    const count = list.filter((m) => m.category_id === category.id).length;
    const message = count > 0
      ? `「${category.name}」 카테고리와 말풍선 ${count}개를 삭제할까요?`
      : `「${category.name}」 카테고리를 삭제할까요?`;
    if (!window.confirm(message)) return;
    setActionError(null);
    try {
      await deleteProfileBubbleCategory(category.id);
      const nextCategories = categories.filter((c) => c.id !== category.id);
      setCategories(nextCategories);
      setList((prev) => prev.filter((m) => m.category_id !== category.id));
      if (selectedCategoryId === category.id) {
        setSelectedCategoryId(nextCategories[0]?.id ?? null);
        setEditingId(null);
      }
    } catch (e) {
      setActionError(actionErrorMessage(e, '카테고리 삭제 실패'));
    }
  }, [categories, list, selectedCategoryId]);

  const handleToggleShown = useCallback(async (category: ProfileBubbleCategory) => {
    const nextShown = !category.is_shown;
    setCategories((prev) => prev.map((c) => (c.id === category.id ? { ...c, is_shown: nextShown } : c)));
    setActionError(null);
    try {
      await setProfileBubbleCategoryShown(category.id, nextShown);
    } catch (e) {
      setCategories((prev) => prev.map((c) => (c.id === category.id ? { ...c, is_shown: category.is_shown } : c)));
      setActionError(actionErrorMessage(e, '표시 설정 실패'));
    }
  }, []);

  const handleDelete = useCallback(async (msg: ProfileBubbleMessage) => {
    if (!window.confirm('이 말풍선을 삭제할까요?')) return;
    setActionError(null);
    try {
      await deleteProfileBubbleMessage(msg.id);
      setList((prev) => prev.filter((m) => m.id !== msg.id));
    } catch (e) {
      setActionError(`삭제 실패: ${e instanceof Error ? e.message : String(e)}`);
    }
  }, []);

  if (!isAdmin && sessionEmail) {
    return (
      <section className="cp-adm-msg-page">
        <p className="cp-adm-msg-no-access">관리자 권한이 필요합니다.</p>
      </section>
    );
  }

  return (
    <section className="cp-adm-msg-page cp-adm-bubble-page" aria-labelledby="cp-adm-bubble-title">
      <div className="cp-adm-msg-page-title-row">
        <h2 id="cp-adm-bubble-title">프로필 말풍선 관리</h2>
        <p className="cp-adm-msg-page-desc">
          체크한 카테고리의 말풍선만 프로필 아이콘에 나옵니다. 여러 카테고리를 함께 고를 수 있고, 그중 하나가 랜덤으로 표시됩니다.
          말풍선을 눌러 바로 수정하세요 (Enter 저장 · Shift+Enter 줄바꿈 · Esc 취소).
        </p>
      </div>

      <article className="cp-dashboard-block cp-adm-msg-section" aria-labelledby="cp-adm-bubble-list-head">
        <h3 className="cp-dashboard-block__head" id="cp-adm-bubble-list-head">
          <span className="cp-dashboard-block__head-title">
            {selectedCategory ? selectedCategory.name : '말풍선 목록'}
            {selectedCategory && (
              <span className="cp-adm-msg-selected-count-badge">{visibleMessages.length}</span>
            )}
          </span>
          {selectedCategory && (
            <button
              type="button"
              className="cp-adm-msg-text-btn cp-adm-bubble-category-delete"
              onClick={() => void handleDeleteCategory(selectedCategory)}
            >
              카테고리 삭제
            </button>
          )}
        </h3>
        <div className="cp-dashboard-block__body cp-adm-msg-section-body">
          {actionError && <p className="cp-adm-msg-send-error">{actionError}</p>}
          {listLoading && <p className="cp-adm-msg-placeholder">불러오는 중...</p>}
          {!listLoading && listError && <p className="cp-adm-msg-error">{listError}</p>}
          {!listLoading && !listError && (
            <>
            <div className="cp-adm-bubble-categories" role="tablist" aria-label="말풍선 카테고리">
              {categories.map((category) => {
                const count = list.filter((m) => m.category_id === category.id).length;
                const selected = category.id === selectedCategoryId;
                return (
                  <div
                    key={category.id}
                    className={`cp-adm-bubble-category${selected ? ' is-active' : ''}${category.is_shown ? '' : ' is-hidden'}`}
                  >
                    <label className="cp-adm-bubble-category-check">
                      <input
                        type="checkbox"
                        checked={category.is_shown}
                        onChange={() => void handleToggleShown(category)}
                        aria-label={`${category.name} 말풍선에 표시`}
                      />
                    </label>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      className="cp-adm-bubble-category-tab"
                      onClick={() => {
                        setSelectedCategoryId(category.id);
                        setEditingId(null);
                      }}
                    >
                      <span>{category.name}</span>
                      <span className="cp-adm-bubble-category-count">{count}</span>
                    </button>
                  </div>
                );
              })}
              {addingCategory ? (
                <input
                  className="cp-adm-bubble-category-input"
                  value={categoryDraft}
                  maxLength={CATEGORY_NAME_MAX}
                  placeholder="카테고리 이름"
                  aria-label="새 카테고리 이름"
                  autoFocus
                  disabled={categorySaving}
                  onChange={(e) => setCategoryDraft(e.target.value)}
                  onBlur={() => void handleCreateCategory(categoryDraft)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                      e.preventDefault();
                      (e.target as HTMLInputElement).blur();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      categoryCancelRef.current = true;
                      setCategoryDraft('');
                      setAddingCategory(false);
                    }
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="cp-adm-bubble-category cp-adm-bubble-category-add"
                  onClick={() => {
                    setCategoryDraft('');
                    setAddingCategory(true);
                  }}
                  disabled={categorySaving}
                >
                  + 카테고리
                </button>
              )}
            </div>
            {!selectedCategory ? (
              <p className="cp-adm-msg-placeholder">카테고리를 추가해 주세요.</p>
            ) : (
            <ul className="cp-adm-bubble-grid">
              {visibleMessages.map((msg) => {
                const isEditing = editingId === msg.id;
                return (
                  <li key={msg.id} className={`cp-adm-bubble-item${isEditing ? ' is-editing' : ''}`}>
                    {isEditing ? (
                      <div className="cp-profile-bubble cp-adm-bubble-block is-editing">
                        <BubbleEditor
                          initialBody={msg.body}
                          saving={savingId === msg.id}
                          onCommit={(body) => void handleCommit(msg.id, body)}
                          onCancel={() => setEditingId(null)}
                        />
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={`cp-profile-bubble cp-adm-bubble-block${savingId === msg.id ? ' is-saving' : ''}`}
                        onClick={() => setEditingId(msg.id)}
                        disabled={savingId === msg.id}
                        title="눌러서 수정"
                      >
                        {msg.body}
                      </button>
                    )}
                    <div className="cp-adm-bubble-item-meta">
                      <span>{formatDateShort(msg.updated_at)}</span>
                      {!isEditing && (
                        <button
                          type="button"
                          className="cp-adm-bubble-delete-btn"
                          onClick={() => void handleDelete(msg)}
                          aria-label="말풍선 삭제"
                          title="삭제"
                        >
                          삭제
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
              <li className="cp-adm-bubble-item">
                {editingId === NEW_BUBBLE_ID ? (
                  <div className="cp-profile-bubble cp-adm-bubble-block is-editing">
                    <BubbleEditor
                      initialBody=""
                      saving={savingId === NEW_BUBBLE_ID}
                      onCommit={(body) => void handleCommit(NEW_BUBBLE_ID, body)}
                      onCancel={() => setEditingId(null)}
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    className="cp-profile-bubble cp-adm-bubble-block cp-adm-bubble-add"
                    onClick={() => setEditingId(NEW_BUBBLE_ID)}
                    disabled={savingId === NEW_BUBBLE_ID}
                    aria-label="말풍선 추가"
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
                    </svg>
                  </button>
                )}
              </li>
            </ul>
            )}
            </>
          )}
        </div>
      </article>
    </section>
  );
};

export default AdminProfileBubblesPage;
